import type { QueryClient } from '@tanstack/react-query';
import type { Audit } from '@amzn/gbsa-helios-typescript-client';

/**
 * Fields that can be patched onto a cached audit. Covers both mutation
 * response audits (Partial<Audit>) and optimistic patches built from
 * command inputs — only overlapping Audit fields take effect.
 */
export type AuditPatch = { [K in keyof Audit]?: Audit[K] | undefined };

export interface CacheSnapshot {
  queryKey: readonly unknown[];
  data: unknown;
}

/**
 * Shape contract for cache entries that contain an `audit` field.
 * Both `GetAuditSummaryCommandOutput` and `GetAuditCommandOutput`
 * satisfy this — the generic helper only reads/writes the `audit`
 * sub-object; any sibling fields (e.g. `questions`) are preserved
 * via the spread.
 */
interface AuditCacheEntry {
  audit?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Builds a query predicate matching cache entries for a given
 * collection key and auditId. Query keys have the shape
 * [collectionKey, { auditId, ... }] (auto-mapped by `createQuery`).
 */
function auditQueryPredicate(collectionKey: string, auditId: string) {
  return (query: { queryKey: readonly unknown[] }): boolean => {
    if (!Array.isArray(query.queryKey) || query.queryKey[0] !== collectionKey) {
      return false;
    }
    const input = query.queryKey[1] as { auditId?: string } | undefined;
    return input?.auditId === auditId;
  };
}

/**
 * Cancels in-flight queries for a specific audit under the given
 * collection key. Used before optimistic updates to prevent races.
 */
export async function cancelAuditQueries(
  queryClient: QueryClient,
  collectionKey: string,
  auditId: string,
): Promise<void> {
  await queryClient.cancelQueries({
    predicate: auditQueryPredicate(collectionKey, auditId),
  });
}

/**
 * Snapshots current cache state and optimistically patches all
 * matching queries for a given collection key + auditId. Returns
 * snapshots for rollback on error.
 */
export function snapshotAndPatchAudit(
  queryClient: QueryClient,
  collectionKey: string,
  auditId: string,
  patch: AuditPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<AuditCacheEntry>({
    predicate: auditQueryPredicate(collectionKey, auditId),
  });

  const snapshots: CacheSnapshot[] = [];
  for (const [queryKey, cachedData] of matchingQueries) {
    if (cachedData?.audit) {
      snapshots.push({ queryKey, data: cachedData });
      queryClient.setQueryData(queryKey, {
        ...cachedData,
        audit: { ...cachedData.audit, ...patch },
      });
    }
  }

  return snapshots;
}

/**
 * Rolls back cached queries to their previous state from snapshots.
 */
export function rollbackAuditCache(
  queryClient: QueryClient,
  snapshots: CacheSnapshot[],
): void {
  for (const { queryKey, data } of snapshots) {
    queryClient.setQueryData(queryKey, data);
  }
}
